import {Box, Divider, MenuItem, Stack, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {
    OFFERED_SURVEY_MEDIA_KINDS,
    SurveyInputPurpose,
    SurveyMediaKind,
    SurveyQuestion
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useTranslation} from "react-i18next";
import {SurveyQuestionTypeElement} from "../../common/uiElements/SurveyQuestionTypeElement";
import {SurveyRequiredMarker, SurveySensitiveMarker} from "../shared/SurveyQuestionMarkers";
import {isScreen} from "../shared/surveyQuestions.util";
import {getSurveyQuestionTypeFacts} from "../shared/surveyQuestionTypeFacts";
import {clearScaleLabels, supportsScaleLabels} from "./scaleLabels.util";
import {PanelField, PanelSection} from "./SurveyPanelSection";
import SurveySidebarPanel from "./SurveySidebarPanel";
import {SwitchRow} from "./SurveySwitchRow";
import SurveyVariableSection, {useSurveySelectedVariable} from "./SurveyVariableSection";
import SurveyVisibilitySection from "./SurveyVisibilitySection";

interface Props {
    question?: SurveyQuestion
    questions: SurveyQuestion[]
    languages: string[]
    language: string
    onLanguageChange: (language: string) => void
    onChange: (question: SurveyQuestion) => void
    scaleLabelsVisible?: boolean
    onScaleLabelsVisibleChange?: (visible: boolean) => void
    disabled?: boolean
}

/** Whether the type has settings of its own, under Configuration. A full Record, so a new type has to answer. */
const HAS_CONFIGURATION: Record<SurveyQuestionType, boolean> = {
    [SurveyQuestionType.WELCOME]: false,
    [SurveyQuestionType.GENERIC_SCORE]: false,
    [SurveyQuestionType.GENERIC_STARS]: false,
    [SurveyQuestionType.MULTIPLE_CHOICE]: true,
    [SurveyQuestionType.PICTURE_CHOICE]: true,
    [SurveyQuestionType.DROPDOWN]: true,
    [SurveyQuestionType.YES_NO]: false,
    [SurveyQuestionType.SHORT_TEXT]: true,
    [SurveyQuestionType.LONG_TEXT]: false,
    [SurveyQuestionType.DATE]: true,
    [SurveyQuestionType.NUMBER]: true,
    [SurveyQuestionType.CHECKLIST]: false,
    [SurveyQuestionType.LEGAL]: true,
    [SurveyQuestionType.UPLOAD_MEDIA]: true,
    [SurveyQuestionType.END_SCREEN]: true,
};

const clampOptionalCount = (value: number | undefined, min: number, max: number) => {
    if (value === undefined || isNaN(value)) return undefined;
    return Math.min(Math.max(value, min), max);
};

function SurveyQuestionDetailsPanel({
    question,
    questions,
    languages,
    language,
    onLanguageChange,
    onChange,
    scaleLabelsVisible,
    onScaleLabelsVisibleChange,
    disabled
}: Props) {
    const {t} = useTranslation();
    const selectedVariable = useSurveySelectedVariable();

    const flag = (code: string) => {
        const src = getIconForLanguage(code);

        if (!src) {
            return (
                <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary'}}>
                    {code.toUpperCase()}
                </Typography>
            );
        }

        return (
            <Box
                component={'img'}
                alt={''}
                src={src}
                sx={{width: 18, height: 18, borderRadius: '50%', objectFit: 'cover'}}
            />
        );
    };

    const languageSwitcher = languages.length > 1 && (
        <PanelField label={t('Editing language')}>
            <TextField
                select
                size={'small'}
                fullWidth
                value={language}
                onChange={(event) => onLanguageChange(event.target.value)}
            >
                {languages.map(code => (
                    <MenuItem key={code} value={code}>
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            {flag(code)}
                            {getNameForLanguage(code, true) || code.toUpperCase()}
                        </Stack>
                    </MenuItem>
                ))}
            </TextField>
        </PanelField>
    );

    if (!question) {
        return (
            <SurveySidebarPanel>
                <Stack gap={3} divider={<Divider/>} sx={{pt: 3}}>
                    <PanelSection title={t('Question settings')}>
                        {languageSwitcher}
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('No question selected.')}
                        </Typography>
                    </PanelSection>
                </Stack>
            </SurveySidebarPanel>
        );
    }

    const screen = isScreen(question);
    const facts = getSurveyQuestionTypeFacts(question.type);
    const patch = (partial: Partial<SurveyQuestion>) => onChange({...question, ...partial});

    const hasConfiguration = !!question.type && !!HAS_CONFIGURATION[question.type];

    return (
        <SurveySidebarPanel>
            <Stack gap={3} divider={<Divider/>} sx={{pt: 3}}>
                {/* First while a chip is selected in the title or description; the question's own
                    settings stay right below it. */}
                {selectedVariable && <SurveyVariableSection {...selectedVariable}/>}

                <PanelSection title={t('Question settings')}>
                    {languageSwitcher}

                    <Box>
                        <SurveyQuestionTypeElement type={question.type}/>
                    </Box>

                    {screen ? (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('Screens frame the survey and collect no answer.')}
                        </Typography>
                    ) : (
                        <Stack gap={0.5}>
                            <SwitchRow
                                checked={!!question.isRequired}
                                onChange={(checked) => patch({isRequired: checked})}
                                label={t('Required')}
                                icon={<SurveyRequiredMarker size={16}/>}
                                tooltip={t('The guest cannot continue without answering this question.')}
                                disabled={disabled}
                            />
                            <SwitchRow
                                checked={!!question.isSensitive}
                                onChange={(checked) => patch({isSensitive: checked})}
                                label={t('Sensitive')}
                                icon={<SurveySensitiveMarker size={16}/>}
                                tooltip={t('Marks the answer as sensitive information, handled with extra care.')}
                                disabled={disabled}
                            />

                            {supportsScaleLabels(question) && (
                                <SwitchRow
                                    checked={!!scaleLabelsVisible}
                                    onChange={(checked) => {
                                        if (!checked) patch(clearScaleLabels());
                                        onScaleLabelsVisibleChange?.(checked);
                                    }}
                                    label={t('Scale labels')}
                                    tooltip={t('Adds wording under the two ends of the scale, e.g. "Not at all" and "Very much".')}
                                    disabled={disabled}
                                />
                            )}
                        </Stack>
                    )}
                </PanelSection>

                {hasConfiguration && (
                    <PanelSection title={t('Configuration')}>
                        {question.type === SurveyQuestionType.SHORT_TEXT && (
                            <PanelField
                                label={t('Input type')}
                                hint={t('Turns the field into an email or phone input for the guest')}
                            >
                                <TextField
                                    select
                                    size={'small'}
                                    fullWidth
                                    disabled={disabled}
                                    value={question.inputPurpose ?? SurveyInputPurpose.TEXT}
                                    onChange={(event) => patch({
                                        inputPurpose: event.target.value as SurveyInputPurpose
                                    })}
                                >
                                    <MenuItem value={SurveyInputPurpose.TEXT}>{t('Text')}</MenuItem>
                                    <MenuItem value={SurveyInputPurpose.EMAIL}>{t('Email')}</MenuItem>
                                    <MenuItem value={SurveyInputPurpose.PHONE}>{t('Phone')}</MenuItem>
                                </TextField>
                            </PanelField>
                        )}

                        {facts?.multipleSelection && (
                            <SwitchRow
                                checked={!!question.allowMultiple}
                                onChange={(checked) => patch({allowMultiple: checked})}
                                label={t('Allow multiple')}
                                tooltip={t('The guest can select more than one option.')}
                                disabled={disabled}
                            />
                        )}

                        {facts?.otherOption && (
                            <SwitchRow
                                checked={!!question.allowOther}
                                onChange={(checked) => patch({allowOther: checked})}
                                label={t('"Other" option')}
                                tooltip={t('Adds one final choice where the guest writes their own answer.')}
                                disabled={disabled}
                            />
                        )}

                        {question.type === SurveyQuestionType.NUMBER && (
                            <>
                                <PanelField label={t('Minimum')}>
                                    <CountTextField
                                        size={'small'}
                                        fullWidth
                                        disabled={disabled}
                                        value={question.minNumber ?? ''}
                                        onChangeValue={(value) => patch({minNumber: value})}
                                    />
                                </PanelField>

                                <PanelField label={t('Maximum')}>
                                    <CountTextField
                                        size={'small'}
                                        fullWidth
                                        disabled={disabled}
                                        value={question.maxNumber ?? ''}
                                        onChangeValue={(value) => patch({maxNumber: value})}
                                    />
                                </PanelField>
                            </>
                        )}

                        {question.type === SurveyQuestionType.DATE && (
                            <>
                                <PanelField label={t('Earliest date')}>
                                    <TextField
                                        size={'small'}
                                        type={'date'}
                                        fullWidth
                                        disabled={disabled}
                                        value={question.minDate ?? ''}
                                        onChange={(event) => patch({minDate: event.target.value || undefined})}
                                    />
                                </PanelField>

                                <PanelField label={t('Latest date')}>
                                    <TextField
                                        size={'small'}
                                        type={'date'}
                                        fullWidth
                                        disabled={disabled}
                                        value={question.maxDate ?? ''}
                                        onChange={(event) => patch({maxDate: event.target.value || undefined})}
                                    />
                                </PanelField>
                            </>
                        )}

                        {question.type === SurveyQuestionType.END_SCREEN && (
                            <>
                                <SwitchRow
                                    checked={!!question.collectEmail}
                                    onChange={(checked) => patch({collectEmail: checked})}
                                    label={t('Collect email')}
                                    tooltip={t('Asks the guest for an email address once their answers are in.')}
                                    disabled={disabled}
                                />

                                <PanelField
                                    label={t('Redirect after submit')}
                                    hint={t('Optional. After submitting, the guest is sent to this address.')}
                                >
                                    <TextField
                                        size={'small'}
                                        fullWidth
                                        disabled={disabled}
                                        placeholder={'https://example.com/thank-you'}
                                        value={question.redirectUrl ?? ''}
                                        onChange={(event) => patch({
                                            redirectUrl: event.target.value || undefined
                                        })}
                                    />
                                </PanelField>

                                {!!question.redirectUrl?.trim() && (
                                    <PanelField label={t('Wait seconds')}>
                                        <CountTextField
                                            size={'small'}
                                            fullWidth
                                            disabled={disabled}
                                            value={question.redirectWaitSeconds ?? ''}
                                            onChangeValue={(value) => patch({
                                                redirectWaitSeconds: clampOptionalCount(value, 0, 60)
                                            })}
                                        />
                                    </PanelField>
                                )}
                            </>
                        )}

                        {question.type === SurveyQuestionType.UPLOAD_MEDIA && (
                            <>
                                <PanelField label={t('Allowed file types')}>
                                    <TextField
                                        select
                                        size={'small'}
                                        fullWidth
                                        disabled={disabled}
                                        SelectProps={{multiple: true}}
                                        value={(question.allowMediaKinds ?? [])
                                            .filter(kind => OFFERED_SURVEY_MEDIA_KINDS.includes(kind))}
                                        onChange={(event) => patch({
                                            allowMediaKinds: event.target.value as unknown as SurveyMediaKind[]
                                        })}
                                    >
                                        <MenuItem value={SurveyMediaKind.IMAGE}>{t('Image')}</MenuItem>
                                        <MenuItem value={SurveyMediaKind.FILE}>{t('Document')}</MenuItem>
                                    </TextField>
                                </PanelField>

                                <SwitchRow
                                    checked={!!question.allowMultiple}
                                    onChange={(checked) => patch({allowMultiple: checked})}
                                    label={t('Allow multiple')}
                                    tooltip={t('The guest can upload more than one file.')}
                                    disabled={disabled}
                                />
                            </>
                        )}

                        {question.type === SurveyQuestionType.LEGAL && (
                            <SwitchRow
                                checked={!!question.requireSignature}
                                onChange={(checked) => patch({requireSignature: checked})}
                                label={t('Require signature')}
                                tooltip={t('The guest must draw a signature in addition to ticking the box.')}
                                disabled={disabled}
                            />
                        )}
                    </PanelSection>
                )}

                {!screen && (
                    <SurveyVisibilitySection
                        question={question}
                        questions={questions}
                        language={language}
                        onChange={onChange}
                        disabled={disabled}
                    />
                )}
            </Stack>
        </SurveySidebarPanel>
    );
}

export default SurveyQuestionDetailsPanel;
